"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Image, Text, useDisclosure, Tag, Tooltip } from "@chakra-ui/react";
import { getApi } from "common/services/api";
import { HasAccess } from "../../../../common/utils/accessUtils";
import { getLoggedInUser, getEffectiveRoleName, isEmployee as checkIsEmployee, isSuperAdmin as checkIsSuperAdmin } from "common/utils/roleUtils";
import CommonCheckTable from "common/components/checktable";
import { useLocation, useNavigate } from "react-router-dom";
import CommonDeleteModel from "common/components/commonDeleteModel";
import { toast } from "react-toastify";
import deleteIcon from "common/assets/img/deleteIcon.png";
import editIcon from "common/assets/img/editIcon.png";
import { constant } from "common/constant";
import DateFormatter from "common/utils/dateFormater";
import { globalStyles } from "common/theme/styles";
import { JobApplicationData } from "services/Recruit/data/jobApplicationData";
import { recruitConstant } from "services/Recruit/constant";
import { putApi } from "common/services/api";
import CustomStatusDropdown from "services/Recruit/components/CustomStatusDropdown/CustomStatusDropdown";
import { accessModuleTitles } from "common/constant";
import { deleteManyApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { applicationStatusOptions } from "services/Recruit/data/dropdownData";
import CustomStatus from "common/components/status/CustomStatus";
import InfoModal from "common/components/infoModel";
import ActionMenu from "common/components/ActionMenu";
import RejectionEmailModal from "services/Recruit/components/RejectionEmailModal";
import { useDispatch, useSelector } from "react-redux";
import { sendRejectionEmail, sendInviteEmail as sendInviteEmailAction } from "common/redux/reducers/applicationSlice";
import { EmailIcon, WarningTwoIcon } from "@chakra-ui/icons";
import InviteEmailModal from "services/Recruit/components/InviteEmailModal";
import {
  getTableFilters,
  saveTableFilters,
  clearTableFilters,
  getInitialFilterState
} from "common/utils/filterUtils";
import { formatNoticePeriod, formatStatusOrStage } from "common/utils/textFormatters";
import { IoWarningOutline } from "react-icons/io5";

const totalRejectedStatuses = ['rejected', 'r1_rejected', 'r2_rejected', 'final_rejected'];

const normalizeApplicationStatus = (rawStatus) => {
  if (!rawStatus) return "";
  const statusStr = String(rawStatus).trim();
  const lower = statusStr.toLowerCase();

  const directMatch = applicationStatusOptions.find(
    (opt) => opt.value.toLowerCase() === lower
  );
  if (directMatch) return directMatch.value;

  const labelMatch = applicationStatusOptions.find(
    (opt) => opt.label.toLowerCase() === lower
  );
  if (labelMatch) return labelMatch.value;

  const cleaned = lower.replace(/\s+/g, "_");
  const cleanedMatch = applicationStatusOptions.find(
    (opt) => opt.value.toLowerCase() === cleaned
  );
  if (cleanedMatch) return cleanedMatch.value;

  return statusStr;
};

const isDeletedEntity = (value) => value === true || value === "true";

const Application = ({ customApiEndpoint, jobOpeningId }) => {
  const title = recruitConstant.createApplications;
  const TABLE_NAME = 'applications'; // Define table name constant
  const [action, setAction] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [edit, setEdit] = useState(false);
  const [eventView, setEventView] = useState(false);
  const [id, setId] = useState("");
  const [selectedId, setSelectedId] = useState();
  const [selectedValues, setSelectedValues] = useState([]);
  const [advanceSearch, setAdvanceSearch] = useState(false);
  const [getTagValuesOutSide, setGetTagValuesOutside] = useState([]);
  const [searchboxOutside, setSearchboxOutside] = useState("");
  const [deleteModel, setDelete] = useState(false);
  const [isImportLead, setIsImportLead] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [displaySearchData, setDisplaySearchData] = useState(false);
  const [searchedData, setSearchedData] = useState([]);
  const [tableData, setTableData] = useState(JobApplicationData);
  const [menuSelectedDeleteItem, setMenuSelectedDeleteItem] = useState([]);
  const colors = globalStyles.colors;
  const [permission] = HasAccess([accessModuleTitles.Applications]);
  const navigate = useNavigate();

  const location = useLocation();
  const parentCandidateId = useMemo(() => {
    if (!customApiEndpoint) return null;
    const match = String(customApiEndpoint).match(/api\/recruitment\/candidates\/([^/]+)\/applications/i);
    return match?.[1] || null;
  }, [customApiEndpoint]);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [isJobOpeningsLoading, setIsJobOpeningsLoading] = useState(true);
  const [deleteInfo, setDeleteInfo] = useState({
    isVisible: false,
    msg: "",
  });
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    message: "",
  });
  const [rejectionEmailModalOpen, setRejectionEmailModalOpen] = useState(false);
  const [inviteEmailModalOpen, setInviteEmailModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const dispatch = useDispatch();
  const { sendingRejectionEmail, sendingInviteEmail } = useSelector((state) => state.applications);
  const isInitialMount = useRef(true);
  const state = location.state;

  const [currentPage, setCurrentPage] = useState(() => {
    const paginationState = JSON.parse(localStorage.getItem("paginationState"));
    return paginationState?.pageName === title && paginationState?.currentPage
      ? paginationState.currentPage
      : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const paginationState = JSON.parse(localStorage.getItem("paginationState"));
    return paginationState?.pageName === title && paginationState?.pageSize
      ? paginationState.pageSize
      : 10;
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("");
  const [searchbox, setSearchbox] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.searchbox || null;
  });
  const [finalSearchedData, setFinalSearchedData] = useState([]);
  const [metadata, setMetadata] = useState({});
  const titleWithCount = metadata?.totalRecords ? `${title} (${metadata.totalRecords})` : title;
  const searchTimeoutRef = useRef(null);
  const fetchVersionRef = useRef(0);

  // Get logged in user and determine effective role (MUST be before useEffect)
  const loggedInUser = getLoggedInUser();
  const effectiveRoleName = getEffectiveRoleName(loggedInUser);
  const isEmployee = checkIsEmployee(loggedInUser);
  const isSuperAdmin = checkIsSuperAdmin(loggedInUser);
  const canDeleteApplicationRecord = useCallback((applicationRecord) => {
    const isCreator = applicationRecord?.createBy?._id === loggedInUser?._id;
    return permission?.delete && (isSuperAdmin || isCreator);
  }, [permission?.delete, isSuperAdmin, loggedInUser?._id]);

  // Fetch job openings on mount
  useEffect(() => {
    const fetchJobOpenings = async () => {
      console.log("[Application] Fetching job openings...");
      setIsJobOpeningsLoading(true);
      try {
        const result = await getApi(API_PATHS.RECRUITMENT_PROFILES);
        console.log("[Application] Job openings fetched:", result?.data?.data?.length);
        setJobOpenings(result?.data?.data || []);
      } catch (e) {
        console.error("[Application] Failed to fetch job openings:", e);
        toast.error("Failed to load job openings");
        setJobOpenings([]); // Set empty array on error
      } finally {
        console.log("[Application] Setting isJobOpeningsLoading to false");
        setIsJobOpeningsLoading(false);
      }
    };
    console.log("[Application] Role check:", {
      effectiveRoleName,
      isEmployee,
      willFetchJobOpenings: !isEmployee
    });

    if (!isEmployee) {
      fetchJobOpenings();
    } else {
      console.log("[Application] User is employee - skipping job openings fetch");
      setIsJobOpeningsLoading(false); // Important: Set to false for employees!
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmployee]); // Re-run when role changes (isEmployee changes when role switches)

  // Navigation handlers - Don't include navigate in dependencies to avoid recreating
  const handleNavigateToApplication = useCallback(
    (values, mode = "overview") => {
      if (isJobOpeningsLoading) {
        return;
      }

      const jobExists = jobOpenings.some((job) => job._id === values?.job?._id);

      if (!jobExists) {
        setDeleteInfo({
          isVisible: true,
          msg: recruitConstant.jobOpeningDeleteMsg,
        });
        return;
      }

      sessionStorage.setItem("mode", mode);
      navigate(`/recruitment/application-create/${values?._id}`);
    },
    [isJobOpeningsLoading, jobOpenings, navigate]
  );
  const handleNavigateToCandidate = useCallback(
    (candidateId, mode = "overview") => {
      if (!candidateId) {
        setDeleteInfo({
          isVisible: true,
          msg: recruitConstant.jobOpeningDeleteMsg.replace(
            "job opening",
            "candidate"
          ),
        });

        return;
      }

      sessionStorage.setItem("mode", mode);
      navigate(`/recruitment/candidate-create/${candidateId}`);
    },
    [navigate]
  );
  const onLinkClick = (id) => {
    if (!id) return;
    navigate(`/recruitment/application-create/${id}`, {
      state: customApiEndpoint
        ? { mode: "overview", from: "candidate-additional-info", candidateId: parentCandidateId, activeMainTab: 1 }
        : { mode: "overview" }
    });
  };
  // State to track if "Created By Me" filter is active
  const [isCreatedByMeActive, setIsCreatedByMeActive] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.isCreatedByMeActive || false;
  });
  // State to track current status filter
  const [currentStatusFilter, setCurrentStatusFilter] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.currentStatusFilter || "";
  });

  // Save filters whenever they change
  useEffect(() => {
    const filters = {
      isCreatedByMeActive,
      currentStatusFilter,
      searchbox: searchbox || "",
    };
    saveTableFilters(TABLE_NAME, filters);
  }, [isCreatedByMeActive, currentStatusFilter, searchbox]);

  const canSelectRow = useCallback((rowData) => {
    return canDeleteApplicationRecord(rowData);
  }, [canDeleteApplicationRecord]);

  const actionHeader = {
    Header: recruitConstant.actions,
    isSortable: false,
    center: true,
    cell: ({ row }) => {
      const applicationData = data.find(
        (item) => item._id === row?.values?._id
      );
      const applicationRecord = applicationData || row?.original || row?.values || {};
      const jobOpeningId = applicationRecord?.job?._id;

      const jobExists = jobOpenings.some((job) => job._id === jobOpeningId);
      const canDelete = canDeleteApplicationRecord(row?.original);
      const isCreator = row?.original?.createBy?._id === loggedInUser?._id;
      const canEdit = permission?.update && (isSuperAdmin || isCreator);
      const jobDeleted = isDeletedEntity(row?.original?.job?.deleted);
      const candidateDeleted = isDeletedEntity(row?.original?.candidateDetails?.deleted);
      const actionsDisabled = jobDeleted || candidateDeleted;
      const rejectionStatuses = ['rejected', 'r1_rejected', 'r2_rejected', 'final_rejected'];
      const normalizedStatus = normalizeApplicationStatus(applicationRecord?.status);
      const canSendRejectionEmail = rejectionStatuses.includes(normalizedStatus) && !applicationRecord?.sentRejectionEmail;
      const canSendInviteEmail = normalizedStatus === 'shortlisted' && !applicationRecord?.sentInviteEmail;
      const isOrphaned = jobDeleted || candidateDeleted;
      const customActions = [];

      if (canSendRejectionEmail && !actionsDisabled) {
        customActions.push({
          label: "Send Rejection Email",
          icon: <EmailIcon />,
          onClick: () => {
            setSelectedApplicationId(row?.values?._id);
            setRejectionEmailModalOpen(true);
          },
          color: "blue.500",
        });
      }

      if (canSendInviteEmail && !actionsDisabled) {
        customActions.push({
          label: "Send Invite Email",
          icon: <EmailIcon />,
          onClick: () => {
            setSelectedApplicationId(row?.values?._id);
            setInviteEmailModalOpen(true);
          },
          color: "teal.500",
        });
      }

      return (
        <Box onClick={(e) => e.stopPropagation()}>
          <ActionMenu
            canView={!isOrphaned}
            canEdit={canEdit && !isOrphaned}
            canDelete={canDelete}
            onView={() => {
              sessionStorage.setItem("mode", "overview");
              navigate(`/recruitment/application-create/${row?.values?._id}`);
            }}
            onEdit={() => {
              sessionStorage.setItem("mode", "edit");
              navigate(`/recruitment/application-create/${row?.values?._id}`);
            }}
            onDelete={() => {
              setDelete(true);
              setSelectedValues([row?.values?._id]);
              setMenuSelectedDeleteItem([row?.values?._id]);
            }}
            customActions={customActions}
          />
        </Box>
      );
    },
  };

  const tableColumns = useMemo(() => {
    return [
      {
        Header: "#",
        accessor: "_id",
        isSortable: false,
        width: 5,
      },
      {
        Header: recruitConstant.applicationId,
        accessor: "customId",
        type: "text",
        formikType: "",
        cell: (cell) => (
          <Box display="flex" alignItems="center" gap={2}>
            <div className="selectOpt">
              <Text>{cell?.value}</Text>
            </div>
          </Box>
        ),
      },
      {
        Header: recruitConstant.applicantName,
        accessor: "candidateName",
        type: "text",
        formikType: "",
        cell: (cell) => {
          const candidateDeleted = isDeletedEntity(
            cell?.row?.original?.candidateDetails?.deleted
          );
          return (
            <Box display="flex" alignItems="center" gap={2}>
              <div className="link">
                <Text
                  cursor="pointer"
                  onClick={() => {
                    const jobDeleted = isDeletedEntity(
                      cell?.row?.original?.jobDeleted
                    ) || isDeletedEntity(cell?.row?.original?.job?.deleted);
                    if (jobDeleted) {
                      setInfoModal({
                        isOpen: true,
                        message:
                          "The job opening related to this application has already been deleted.",
                      });
                      return;
                    }
                    if (candidateDeleted) {
                      setInfoModal({
                        isOpen: true,
                        message:
                          "The candidate related to this application has already been deleted.",
                      });
                      return;
                    }
                    if (customApiEndpoint) {
                      const applicationId = cell?.row?.original?._id;
                      if (!applicationId) {
                        setInfoModal({
                          isOpen: true,
                          message:
                            "The application could not be opened.",
                        });
                        return;
                      }

                      onLinkClick(applicationId);
                      return;
                    }

                    const candidateId =
                      cell?.row?.original?.candidateDetails?._id;
                    if (!candidateId) {
                      setInfoModal({
                        isOpen: true,
                        message:
                          "The candidate related to this application has already been deleted.",
                      });
                      return;
                    }

                    handleNavigateToCandidate(candidateId, "overview");
                  }}
                  color={colors.cta["100"]}
                  _hover={{ textDecoration: "underline" }}
                >
                  {cell?.value}
                </Text>
              </div>
              {candidateDeleted && (
                <Tooltip label="Candidate Deleted" hasArrow placement="right">
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <IoWarningOutline className="overdue-icon" />
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },

      {
        Header: "Posting Title ",
        accessor: "title",
        cell: (cell) => {
          const title = cell?.row?.original?.title; // Use the job posting title
          const jobDeleted = isDeletedEntity(
            cell?.row?.original?.jobDeleted
          ) || isDeletedEntity(cell?.row?.original?.job?.deleted);

          const formatJobTitle = (title) => {
            if (!title) return "-";
            return title
              .toString()
              .trim()
              .replace(/^Other[-\s]*/i, "")
              .replace(/[-_]+/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (char) => char.toUpperCase());
          };

          const readableTitle = formatJobTitle(title);

          return (
            <Box display="flex" alignItems="center" gap={2}>
              <Text
                fontWeight="500"
                fontSize="sm"
                color="gray.800"
                textTransform="capitalize"
                title={readableTitle}
              >
                {readableTitle}
              </Text>
              {jobDeleted && (
                <Tooltip label="Job Deleted" hasArrow placement="right">
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <IoWarningOutline className="overdue-icon" />
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },

      {
        Header: recruitConstant.currentLocation,
        accessor: "candidateCity",
        type: "text",
        formikType: "",
        cell: (cell) => (
          <Box>
            <div className="selectOpt">
              <Text>{cell?.value || '-'}</Text>
            </div>
          </Box>
        ),
      },
      {
        Header: isEmployee ? recruitConstant.experience : "Experience (Yrs)",
        accessor: "experienceRange",
        type: "text",
        formikType: "",
        cell: (cell) => (
          <Box>
            <div className="selectOpt">
              <Text>{cell?.value || "-"}</Text>
            </div>
          </Box>
        ),
      },
      {
        Header: recruitConstant.noticePeriod,
        accessor: "noticePeriod",
        type: "text",
        formikType: "",
        cell: (cell) => (
          <Box>
            <div className="selectOpt">
              <Text>{formatNoticePeriod(cell?.value) || "-"}</Text>
            </div>
          </Box>
        ),
      },
      {
        Header: recruitConstant.status,
        accessor: "status",
        type: "select",
        formikType: "",
        cell: (cell) => {
          const normalizedStatus = normalizeApplicationStatus(cell?.value);
          const statusLabel = applicationStatusOptions.find(
            (option) => option.value === normalizedStatus
          )?.label || formatStatusOrStage(cell?.value);

          return (
            <CustomStatus
              statusText={statusLabel}
            />
          );
        },
      },
      {
        Header: recruitConstant.createdAt,
        accessor: "createdAt",
        type: "text",
        formikType: "",
        cell: (cell) => (
          <Box>
            <Text me="10px" fontSize="sm" fontWeight="700">
              <span>{DateFormatter(cell?.value)}</span>
            </Text>
          </Box>
        ),
      },
      {
        Header: recruitConstant.modifiedAt,
        accessor: "updatedAt",
        type: "text",
        formikType: "",
        cell: (cell) => (
          <Box>
            <Text me="10px" fontSize="sm" fontWeight="700">
              <span>{DateFormatter(cell?.value)}</span>
            </Text>
          </Box>
        ),
      },
      ...(permission?.update || permission?.delete || permission?.view ? [actionHeader] : []),
    ];
  }, [
    handleNavigateToApplication,
    handleNavigateToCandidate,
    actionHeader,
    colors,
    permission,
  ]);

  // Helper function to get status from URL
  const getStatusFromURL = () => {
    // Try location.search first (works with React Router)
    const params = new URLSearchParams(location.search);
    let status = params.get("status");

    // If not found in location.search, try parsing from hash (for hash routing)
    if (!status && window.location.hash) {
      const hashParts = window.location.hash.split("?");
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        status = hashParams.get("status");
      }
    }

    return status || "";
  };

  // Helper function to get created date from URL
  const getCreatedDateFromURL = () => {
    // Try location.search first
    const params = new URLSearchParams(location.search);
    let createdDate = params.get("createdDate");

    // If not found in location.search, try parsing from hash (for hash routing)
    if (!createdDate && window.location.hash) {
      const hashParts = window.location.hash.split("?");
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        createdDate = hashParams.get("createdDate");
      }
    }

    return createdDate || "";
  };

  const getDateRangeFromURL = () => {
    const params = new URLSearchParams(location.search);
    let startDate = params.get("startDate");
    let endDate = params.get("endDate");

    if ((!startDate || !endDate) && window.location.hash) {
      const hashParts = window.location.hash.split("?");
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        startDate = startDate || hashParams.get("startDate");
        endDate = endDate || hashParams.get("endDate");
      }
    }

    return {
      startDate: startDate || "",
      endDate: endDate || "",
    };
  };

  const fetchData = useCallback(
    async (
      page = currentPage,
      limit = pageSize,
      sortKey = sortColumn,
      order = sortOrder,
      searchTerm = "",
      fetchAllData = false,
      status = "",
      createBy = ""
    ) => {
      // Always get createdDate from URL (it's a special filter that comes from navigation)
      const createdDate = getCreatedDateFromURL();
      const { startDate, endDate } = getDateRangeFromURL();
      setIsLoading(true);

      let endpoint = customApiEndpoint
        ? `${customApiEndpoint}?page=${page}&limit=${limit}`
        : `api/recruitment/application/?page=${page}&limit=${limit}`;

      if (fetchAllData) {
        endpoint = customApiEndpoint
          ? `${customApiEndpoint}?allData=true`
          : `api/recruitment/application/?allData=true`;
      }

      if (sortKey) {
        const sortParam = order === "desc" ? `-${sortKey}` : sortKey;
        endpoint += `&sort=${sortParam}`;
      }

      if (searchTerm) {
        endpoint += `&search=${encodeURIComponent(searchTerm)}`;
      }

      if (status) {
        const statusParam = status === 'total_rejected'
          ? totalRejectedStatuses.join(',')
          : status;
        const statusParamLower = statusParam.toLowerCase();
        console.log('[Application fetchData] Status filter:', { original: status, resolved: statusParamLower });
        endpoint += `&status=${encodeURIComponent(statusParamLower)}`;
      }

      if (createdDate) {
        endpoint += `&createdDate=${encodeURIComponent(createdDate)}`;
      }

      if (startDate) {
        endpoint += `&startDate=${encodeURIComponent(startDate)}`;
      }

      if (endDate) {
        endpoint += `&endDate=${encodeURIComponent(endDate)}`;
      }

      if (createBy) {
        endpoint += `&createBy=${createBy}`;
      }

      console.log("[Application] Fetching data:", { endpoint, page, limit, status });

      const currentVersion = ++fetchVersionRef.current;

      try {
        const result = await getApi(endpoint);

        if (currentVersion !== fetchVersionRef.current) {
          return;
        }

        console.log("[Application] API Response:", {
          status: result?.status,
          dataLength: result?.data?.data?.length,
          metadata: result?.data?.metadata
        });

        // Ensure data is an array
        const responseData = result?.data?.data;
        if (!Array.isArray(responseData)) {
          console.error("[Application] Invalid data format - expected array, got:", typeof responseData);
          setData([]);
          setMetadata(result?.data?.metadata || {});
          return;
        }

        const flatData = responseData.map((item) => ({
          ...item,
          candidateName: item.candidateDetails?.name,
          candidateCity: item.candidateDetails?.city,
          experienceRange: item.candidateDetails?.experienceRange,
          noticePeriod: item.candidateDetails?.noticePeriod,
          title: item.job?.postingTitle,
          jobDeleted: item.job?.deleted || false,
        }));

        console.log("[Application] Processed data:", flatData.length, "records");

        if (fetchAllData) {
          setFinalSearchedData(flatData);
        } else {
          setData(flatData || []);
          setMetadata(result?.data?.metadata || {});
        }
      } catch (e) {
        if (currentVersion === fetchVersionRef.current) {
          console.error("[Application] Error fetching job applications:", {
            error: e,
            message: e?.message,
            response: e?.response?.data,
            status: e?.response?.status
          });

          // Show specific error message if available
          const errorMsg = e?.response?.data?.message ||
            e?.response?.statusText ||
            recruitConstant.errorMsg;
          toast.error(errorMsg);

          // Set empty data on error
          setData([]);
          setMetadata({});
        }
      } finally {
        if (currentVersion === fetchVersionRef.current) {
          console.log("[Application] Setting isLoading to false");
          setIsLoading(false);
        }
      }
    },
    [currentPage, pageSize, sortColumn, sortOrder, customApiEndpoint]
  );

  const handleSearchChange = (term) => {
    const trimmedTerm = term?.trim() || "";
    setSearchbox(trimmedTerm);

    const pageToFetch = trimmedTerm === "" ? currentPage : 1;
    const statusFromURL = getStatusFromURL();
    if (trimmedTerm !== "" && currentPage !== 1) {
      setCurrentPage(1);
    }

    // Get createBy value if filter is active
    const createBy = isCreatedByMeActive ? loggedInUser?._id : "";

    // Use current status filter if available, otherwise use URL status
    const statusToUse = currentStatusFilter || statusFromURL;

    fetchData(
      pageToFetch,
      pageSize,
      sortColumn,
      sortOrder,
      trimmedTerm || "",
      false,
      statusToUse,
      createBy
    );
  };

  const handleSortChange = (accessorKey) => {
    let newSortOrder = "asc";

    if (sortColumn === accessorKey) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortColumn(accessorKey);
    setSortOrder(newSortOrder);

    const statusFromURL = getStatusFromURL();

    // Get createBy value if filter is active
    const createBy = isCreatedByMeActive ? loggedInUser?._id : "";

    // Use current status filter if available, otherwise use URL status
    const statusToUse = currentStatusFilter || statusFromURL;

    fetchData(
      currentPage,
      pageSize,
      accessorKey,
      newSortOrder,
      searchbox || "",
      false,
      statusToUse,
      createBy
    );
  };

  const handleDeleteApplication = async (ids) => {
    try {
      setDelete(false);
      const response = await deleteManyApi(
        "api/recruitment/application/deleteMany",
        ids
      );
      if (response.status === 200) {
        setSelectedValues([]);
        const statusFromURL = getStatusFromURL();

        // Get createBy value if filter is active
        const createBy = isCreatedByMeActive ? loggedInUser?._id : "";

        // Use current status filter if available, otherwise use URL status
        const statusToUse = currentStatusFilter || statusFromURL;

        fetchData(
          currentPage,
          pageSize,
          sortColumn,
          sortOrder,
          searchbox || "",
          false,
          statusToUse,
          createBy
        );
        toast.success(
          `${menuSelectedDeleteItem.length > 0
            ? constant.application
            : selectedValues && selectedValues.length > 1
              ? `${constant.application}s`
              : constant.application
          } ${constant.deletedSuccessfully}`
        );
        setAction((pre) => !pre);
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setMenuSelectedDeleteItem([]);
    }
  };

  const [columns, setColumns] = useState([...tableColumns]);
  const defaultVisibleHeaders = [
    "#",
    recruitConstant.applicationId,
    recruitConstant.applicantName,
    "Posting Title ",
    recruitConstant.status,
    isEmployee ? recruitConstant.experience : "Experience (Yrs)",
    recruitConstant.noticePeriod,
    recruitConstant.currentLocation,
    recruitConstant.createdAt,
    recruitConstant.actions,
  ];
  const defaultVisibleColumns = defaultVisibleHeaders
    .map((header) => tableColumns.find((column) => column.Header === header))
    .filter(Boolean);
  const [selectedColumns, setSelectedColumns] = useState([...defaultVisibleColumns]);

  useEffect(() => {
    if (isInitialMount.current && tableColumns.length > 0) {
      setSelectedColumns([...defaultVisibleColumns]);
      isInitialMount.current = false;
    }
  }, [tableColumns, defaultVisibleColumns]);

  // Memoize dataColumn
  const dataColumn = useMemo(
    () =>
      columns?.filter((item) =>
        selectedColumns?.find(
          (column) =>
            (column?.accessor && column.accessor === item.accessor) ||
            column?.Header === item?.Header
        )
      ),
    [columns, selectedColumns]
  );
  const handleCreatedByMe = useCallback(async () => {
    const user = localStorage.getItem("user");
    const userId = JSON.parse(user)?._id;

    // Toggle the filter
    const newState = !isCreatedByMeActive;
    setIsCreatedByMeActive(newState);

    // If activating filter, fetch with createBy; if deactivating, fetch without
    const createBy = newState ? userId : "";

    // Get current filters
    const statusFromURL = getStatusFromURL();
    const statusToUse = currentStatusFilter || statusFromURL;

    // Fetch with current search and status filter maintained
    await fetchData(
      currentPage,
      pageSize,
      sortColumn,
      sortOrder,
      searchbox || "",
      false,
      statusToUse,
      createBy
    );
  }, [
    isCreatedByMeActive,
    currentPage,
    pageSize,
    sortColumn,
    sortOrder,
    searchbox,
    currentStatusFilter,
  ]);

  const handleStatusFilterChange = useCallback(
    (selectedStatus) => {
      // Convert label to value if needed (dropdown passes labels, but we need values)
      const statusOption = applicationStatusOptions.find(
        opt => opt.label === selectedStatus || opt.value === selectedStatus
      );
      const statusValue = statusOption ? statusOption.value : selectedStatus.toLowerCase();
      const statusParam = statusValue === 'total_rejected'
        ? totalRejectedStatuses.join(',')
        : statusValue;

      // Update current status filter state with the value (not label)
      setCurrentStatusFilter(statusValue);
      setCurrentPage(1);

      // Update URL to reflect the filter change (so it persists and matches the filter)
      const params = new URLSearchParams(location.search);
      if (statusValue) {
        params.set("status", statusValue);
      } else {
        params.delete("status");
      }
      // Build the new path with query parameters
      const newPath = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      // Update URL without triggering a page reload
      navigate(newPath, { replace: true });

      // Get createBy value if filter is active
      const createBy = isCreatedByMeActive ? loggedInUser?._id : "";

      // Include current search term
      fetchData(
        1,
        pageSize,
        sortColumn,
        sortOrder,
        searchbox || "",
        false,
        statusValue,
        createBy
      );
    },
    [
      isCreatedByMeActive,
      loggedInUser?._id,
      pageSize,
      sortColumn,
      sortOrder,
      searchbox,
    ]
  );

  // Initialize status filter from URL on mount/URL change
  useEffect(() => {
    const statusFromQuery = getStatusFromURL();
    // Always update from URL if URL has status (to handle navigation from dashboard)
    // If URL has no status, clear the filter
    if (statusFromQuery) {
      setCurrentStatusFilter(statusFromQuery);
    } else {
      // Clear filter if URL doesn't have status parameter
      setCurrentStatusFilter("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash]);

  // Fetch data when filters or pageSize change (NOT on pagination - that's handled by checktable)
  // Note: currentPage is intentionally NOT in dependencies to let checktable handle pagination
  useEffect(() => {
    // Get createBy value if filter is active
    const createBy = isCreatedByMeActive ? loggedInUser?._id : "";
    const statusFromQuery = getStatusFromURL();
    // Prioritize URL status over currentStatusFilter (URL is source of truth for navigation)
    const statusToUse = statusFromQuery || currentStatusFilter;

    // When filters change, reset to page 1 and fetch
    // Pagination changes are handled by checktable's handlePaginationChange
    fetchData(
      1, // Always start from page 1 when filters change
      pageSize,
      sortColumn,
      sortOrder,
      searchbox || "",
      false,
      statusToUse,
      createBy
    );
  }, [
    isCreatedByMeActive, // Created by me filter toggle
    currentStatusFilter, // Status filter change
    // Note: searchbox and currentPage are NOT here - handled by checktable/manual trigger
  ]);

  function closeDeleteModal() {
    setMenuSelectedDeleteItem([]);
    setDelete(false);
  }

  const handleSendRejectionEmail = async (templateId) => {
    try {
      const application = data.find(app => app._id === selectedApplicationId);
      const result = await dispatch(sendRejectionEmail(selectedApplicationId, templateId, application?.candidateName, application?.title));

      if (result.type === 'applications/sendRejectionEmail/fulfilled') {
        setRejectionEmailModalOpen(false);
        setSelectedApplicationId(null);

        // Refresh data to reflect updated sentRejectionEmail field
        const createBy = isCreatedByMeActive ? loggedInUser?._id : "";
        const statusFromURL = getStatusFromURL();
        const statusToUse = currentStatusFilter || statusFromURL;

        fetchData(
          currentPage,
          pageSize,
          sortColumn,
          sortOrder,
          searchbox || "",
          false,
          statusToUse,
          createBy
        );
      }
    } catch (error) {
      console.error("Failed to send rejection email:", error);
    }
  };

  const handleSendInviteEmail = async ({
    templateId,
    interviewDate,
    interviewTime,
    interviewAddress,
    googleMapLink,
  }) => {
    try {
      const result = await dispatch(
        sendInviteEmailAction(selectedApplicationId, {
          templateId,
          interviewDate,
          interviewTime,
          interviewAddress,
          googleMapLink,
        })
      );

      if (result.type === "applications/sendInviteEmail/fulfilled") {
        setInviteEmailModalOpen(false);
        setSelectedApplicationId(null);

        const createBy = isCreatedByMeActive ? loggedInUser?._id : "";
        const statusFromURL = getStatusFromURL();
        const statusToUse = currentStatusFilter || statusFromURL;

        fetchData(
          currentPage,
          pageSize,
          sortColumn,
          sortOrder,
          searchbox || "",
          false,
          statusToUse,
          createBy
        );
      }
    } catch (error) {
      console.error("Failed to send invite email:", error);
    }
  };

  return (
    <div className={customApiEndpoint ? "detailPageTable" : ""}>
      <CommonCheckTable
        title={title}
        showInlineTitle={true}
        inlineTitle={titleWithCount}
        isLoading={isLoading || isJobOpeningsLoading}
        columnData={columns}
        dataColumn={dataColumn}
        allData={data}
        checkBox={canSelectRow}
        tableData={data}
        setSearchDisplay={setDisplaySearchData}
        tableCustomFields={[]}
        access={permission}
        action={action}
        setAction={setAction}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        setDelete={setDelete}
        fetchData={fetchData}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        searchboxOutside={searchboxOutside}
        sortOrder={sortOrder}
        searchbox={searchbox}
        setSearchbox={setSearchbox}
        setSearchboxOutside={setSearchboxOutside}
        finalSearchedData={finalSearchedData}
        setFinalSearchedData={setFinalSearchedData}
        metadata={metadata}
        handleSortChange={handleSortChange}
        handleSearchChange={handleSearchChange}
        filterStatusOptions={applicationStatusOptions}
        statusFilter={true}
        showCreatedByMeFilter={isEmployee ? false : true}
        handleCreatedByMe={handleCreatedByMe}
        isCreatedByMeActive={isCreatedByMeActive}
        createdByMeLabel="My Applications"
        handleStatusFilterChange={handleStatusFilterChange}
        sortColumn={sortColumn}
        currentStatusFilter={currentStatusFilter}
        createByFilter={isCreatedByMeActive ? loggedInUser?._id : ""}
        onLinkClick={onLinkClick}
        isHideNavbarAction={!customApiEndpoint}
        isDynamic={true}
      />

      <CommonDeleteModel
        isOpen={deleteModel}
        onClose={() => closeDeleteModal()}
        type={
          menuSelectedDeleteItem.length > 0
            ? constant.application
            : selectedValues && selectedValues.length > 1
              ? `${constant.application}s`
              : constant.application
        }
        handleDeleteData={handleDeleteApplication}
        ids={selectedValues}
        onCancel={() => setSelectedValues([])}
      />
      <InfoModal
        onClose={() => setDeleteInfo({ isVisible: false, msg: "" })}
        isOpen={deleteInfo.isVisible}
        message={deleteInfo.msg}
      />
      <InfoModal
        onClose={() => setInfoModal({ isOpen: false, message: "" })}
        isOpen={infoModal.isOpen}
        message={infoModal.message}
      />
      <RejectionEmailModal
        isOpen={rejectionEmailModalOpen}
        onClose={() => {
          setRejectionEmailModalOpen(false);
          setSelectedApplicationId(null);
        }}
        onSend={handleSendRejectionEmail}
        isLoading={sendingRejectionEmail}
      />
      <InviteEmailModal
        isOpen={inviteEmailModalOpen}
        onClose={() => {
          setInviteEmailModalOpen(false);
          setSelectedApplicationId(null);
        }}
        onSend={handleSendInviteEmail}
        isLoading={sendingInviteEmail}
      />
    </div>
  );
};

export default Application;
